import React, { useEffect, useRef } from 'react';

/**
 * McaDrawer — Off-canvas / slide-out drawer with scrim, close button, and keyboard support.
 * Same behaviour as Modal but renders as an off-canvas panel on the right (responsive).
 * 
 * Props:
 *   - open (bool): Control drawer visibility
 *   - onClose (fn): Called when drawer closes (scrim click, Esc, data-close button)
 *   - title (string): Drawer title (h3)
 *   - subtitle (string): Optional subtitle (appears below title)
 *   - children (node): Drawer body content
 *   - footer (node): Optional footer slot (buttons, etc.)
 *   - static (bool): If true, scrim click doesn't close
 *   - ariaLabelledBy (string): aria-labelledby
 */
export function McaDrawer({ open, onClose, title, subtitle, children, footer, static: isStatic, ariaLabelledBy }) {
  const overlayRef = useRef(null);
  const titleId = useRef(null);

  useEffect(() => {
    if (title && !ariaLabelledBy) {
      titleId.current = 'mca-drawer-title-' + Math.random().toString(36).slice(2);
    }
  }, [title, ariaLabelledBy]);

  useEffect(() => {
    const ov = overlayRef.current;
    if (!ov) return;

    if (open) {
      ov.classList.add('is-open');
      ov.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        const focusable = ov.querySelector('input, button, select, textarea, [tabindex="0"]');
        if (focusable) focusable.focus();
      }, 60);
    } else {
      ov.classList.remove('is-open');
      ov.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }, [open]);

  const handleEscape = (e) => {
    if (e.key === 'Escape' && open) {
      onClose?.();
    }
  };

  const handleScrimClick = (e) => {
    if (e.target === e.currentTarget && !isStatic) {
      onClose?.();
    }
  };

  const handleCloseClick = () => {
    onClose?.();
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open, onClose]);

  return (
    <div
      ref={overlayRef}
      data-overlay
      aria-hidden={!open}
      className="mca-overlay"
      onClick={handleScrimClick}
    >
      <div className="mca-scrim" data-scrim />
      <div
        className="mca-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy || titleId.current}
      >
        {/* HEAD: title + subtitle + close */}
        {(title || subtitle) && (
          <div className="mca-drawer__head">
            <div>
              {title && (
                <h3 id={titleId.current}>{title}</h3>
              )}
              {subtitle && (
                <p>{subtitle}</p>
              )}
            </div>
            <button
              className="mca-overlay-close"
              onClick={handleCloseClick}
              aria-label="Close"
              type="button"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* BODY: main content */}
        {children && (
          <div className="mca-drawer__body">
            {children}
          </div>
        )}

        {/* FOOT: action buttons */}
        {footer && (
          <div className="mca-drawer__foot">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default McaDrawer;
